import { useState, useEffect, useRef, Fragment } from 'react';
import { Icon } from './Icons.jsx';
import { getCatalog } from '../api/labs.js';
import { brl } from '../utils/format.js';

export default function ExamSearchScreen({ onBack, onBuy, onUploadPrescription }) {
  const [q, setQ] = useState('');
  const [focused, setFocused] = useState(false);
  const [matches, setMatches] = useState([]);
  const [searching, setSearching] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => inputRef.current?.focus(), 80);
    return () => clearTimeout(t);
  }, []);

  // Busca real no catálogo, com debounce
  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setMatches([]); setSearching(false); return; }
    setSearching(true);
    const t = setTimeout(() => {
      getCatalog({ q: term, limit: 12 })
        .then(data => setMatches(data || []))
        .catch(() => setMatches([]))
        .finally(() => setSearching(false));
    }, 350);
    return () => clearTimeout(t);
  }, [q]);

  const showDropdown = focused && q.trim().length > 0;
  const showResults = q.trim().length > 0;

  return (
    <div className="screen" style={{ background: 'var(--bg)' }}>
      {/* header */}
      <div className="mob-header" style={{ padding: '12px 16px 14px' }}>
        <button className="mob-back" onClick={onBack} aria-label="Voltar">
          <Icon.chevL size={20}/>
        </button>
        <div className="mob-header-title">Pesquisar exames</div>
      </div>

      {/* campo de busca */}
      <div style={{ padding: '14px 16px 6px', background: 'var(--white)', borderBottom: '1px solid var(--border)', position: 'relative' }}>
        <div style={{ position: 'relative' }}>
          <Icon.search size={18} color="var(--text-subtle)" style={{ position: 'absolute', left: 14, top: 16 }}/>
          <input
            ref={inputRef}
            className="input"
            value={q}
            onChange={e => setQ(e.target.value)}
            onFocus={() => setFocused(true)}
            onBlur={() => setTimeout(() => setFocused(false), 150)}
            placeholder="Digite o nome ou código do exame…"
            style={{ paddingLeft: 42, paddingRight: q ? 42 : 14 }}
          />
          {q && (
            <button
              onClick={() => { setQ(''); inputRef.current?.focus(); }}
              aria-label="Limpar"
              style={{ position: 'absolute', right: 10, top: 13, padding: 6, color: 'var(--text-muted)' }}
            >
              <Icon.x size={18}/>
            </button>
          )}
        </div>

        <div style={{ fontSize: 11.5, color: 'var(--text-subtle)', marginTop: 8, fontFamily: 'var(--font-display)', letterSpacing: '0.02em' }}>
          {showResults
            ? searching
              ? 'Buscando…'
              : <>{matches.length} {matches.length === 1 ? 'exame encontrado' : 'exames encontrados'}</>
            : 'Busque pelo nome ou código TUSS do exame.'}
        </div>

        {onUploadPrescription && (
          <button
            className="btn btn-primary"
            onClick={onUploadPrescription}
            style={{ width: '100%', marginTop: 12 }}
          >
            <Icon.file size={18} color="var(--white)"/> Buscar do pedido médico
          </button>
        )}

        {/* dropdown de sugestões */}
        {showDropdown && matches.length > 0 && (
          <div style={{
            position: 'absolute', left: 16, right: 16, top: 'calc(100% - 24px)',
            background: 'var(--white)', borderRadius: 'var(--r-md)',
            border: '1px solid var(--border)',
            boxShadow: '0 14px 30px var(--overlay-navy-12)',
            zIndex: 30, maxHeight: 280, overflowY: 'auto',
          }}>
            {matches.slice(0, 6).map(e => (
              <button
                key={e.code}
                onMouseDown={(ev) => { ev.preventDefault(); setQ(e.name); }}
                style={{
                  width: '100%', textAlign: 'left',
                  padding: '11px 14px', borderBottom: '1px solid var(--border)',
                  display: 'flex', alignItems: 'center', gap: 10,
                }}
              >
                <Icon.flask size={16} color="var(--brand-primary)"/>
                <span style={{ flex: 1, minWidth: 0, fontSize: 13.5, fontWeight: 500, color: 'var(--midnight)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  <Highlight text={e.name} term={q}/>
                </span>
                <span style={{ fontSize: 10.5, color: 'var(--text-subtle)', fontFamily: 'JetBrains Mono, monospace' }}>
                  {e.code.split('-')[0]}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* resultados */}
      <div style={{ flex: 1, padding: '8px 16px 24px' }}>
        {!showResults && <EmptyHint onPick={setQ}/>}
        {showResults && !searching && matches.length === 0 && <NoResults q={q}/>}
        {showResults && matches.length > 0 && (
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {matches.map(e => (
              <li key={e.code} style={{
                display: 'grid', gridTemplateColumns: '1fr auto', gap: 10, alignItems: 'center',
                padding: '14px 2px', borderBottom: '1px solid var(--border)',
              }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 10.5, color: 'var(--text-subtle)', fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.02em' }}>
                    {e.code}
                  </div>
                  <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--midnight)', marginTop: 2, lineHeight: 1.25 }}>
                    <Highlight text={e.name} term={q}/>
                  </div>
                  <div style={{ marginTop: 3, display: 'flex', alignItems: 'baseline', gap: 6 }}>
                    <span style={{ fontSize: 10.5, color: 'var(--text-subtle)', textTransform: 'uppercase', letterSpacing: '0.04em', fontFamily: 'var(--font-display)' }}>
                      a partir de
                    </span>
                    <span style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 15, color: 'var(--brand-primary)' }}>
                      R$ {brl(e.price)}
                    </span>
                  </div>
                </div>
                <button onClick={() => onBuy(e)} style={{
                  padding: '9px 16px', borderRadius: 99,
                  background: 'var(--midnight)', color: 'var(--white)',
                  fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 13,
                  minHeight: 38, display: 'inline-flex', alignItems: 'center', gap: 6,
                }}>
                  Comprar <Icon.arrowR size={14} color="var(--white)"/>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function Highlight({ text, term }) {
  const t = term.trim();
  if (!t) return text;
  try {
    const re = new RegExp('(' + t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + ')', 'ig');
    const parts = text.split(re);
    return parts.map((p, i) =>
      re.test(p)
        ? <mark key={i} style={{ background: 'var(--brand-primary-soft)', color: 'var(--brand-primary-dark)', padding: '0 2px', borderRadius: 3 }}>{p}</mark>
        : <Fragment key={i}>{p}</Fragment>
    );
  } catch {
    return text;
  }
}

function EmptyHint({ onPick }) {
  const suggestions = ['Vitamina D', 'Hemograma', 'TSH', 'Glicemia', 'Ultrassom', 'Colesterol'];
  return (
    <div style={{ padding: '24px 4px 0' }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', fontFamily: 'var(--font-display)', marginBottom: 12 }}>
        Buscas populares
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {suggestions.map(s => (
          <button key={s} onClick={() => onPick(s)} style={{
            padding: '8px 14px', borderRadius: 99,
            background: 'var(--white)', border: '1px solid var(--border)',
            fontSize: 13, color: 'var(--midnight)', fontWeight: 500,
          }}>{s}</button>
        ))}
      </div>

      <div style={{ marginTop: 30, padding: 16, borderRadius: 'var(--r-md)', background: 'var(--marble-soft)', display: 'flex', gap: 10 }}>
        <Icon.info size={18} color="var(--marble)" style={{ flexShrink: 0, marginTop: 1 }}/>
        <div style={{ fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
          Não achou o exame? Você também pode buscar pelo{' '}
          <strong style={{ color: 'var(--midnight)' }}>código TUSS</strong> que aparece no seu pedido médico.
        </div>
      </div>
    </div>
  );
}

function NoResults({ q }) {
  return (
    <div style={{ padding: '40px 16px', textAlign: 'center' }}>
      <div style={{
        width: 56, height: 56, borderRadius: 99, margin: '0 auto 14px',
        background: 'var(--marble-soft)', display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Icon.search size={26} color="var(--text-subtle)"/>
      </div>
      <div style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 15, color: 'var(--midnight)' }}>
        Nenhum exame para "{q}"
      </div>
      <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>
        Tente buscar pelo nome ou pelo código do exame.
      </div>
    </div>
  );
}
